<script setup>
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import QRCode from 'qrcodejs2'
import getUA from '@utils/getUA'
import todayAxios from '@utils/global/todayAxiosGlobal'

const route = useRoute()
const isPC = ref(window.innerWidth >= 768)

const appStoreURL =
  'https://apps.apple.com/app/apple-store/id6446071834?pt=126093046&ct=officialsite&mt=8'

const apkURL =
  'https://play.google.com/store/apps/details?id=com.oceangalaxy.camera.new&referrer=utm_source%3Dofficialsite'

onMounted(() => {
  if (isPC.value) {
    renderQRCode()
  } else {
    init()
  }
})

function renderQRCode() {
  new QRCode(document.getElementById('qrcode'), {
    text: window.location.href,
    width: 200,
    height: 200,
    colorDark: '#000000',
    colorLight: '#ffffff',
    correctLevel: QRCode.CorrectLevel.L
  })
}

function collectFingerprint() {
  const userAgent = navigator.userAgent
  const screenResolution = `${screen.width}x${screen.height}`
  const pixelRatio = window.devicePixelRatio
  const timezone = -new Date().getTimezoneOffset()
  const language = navigator.language
  const timestamp = Date.now()

  const deviceModelMatch = userAgent.match(/\((.*?)\)/)
  const deviceInfo = deviceModelMatch ? deviceModelMatch[1] : ''
  const deviceModel = deviceInfo.split(';')[0]?.trim() || ''
  const systemVersion = deviceInfo.split(';')[1]?.trim() || ''

  const [screenWidth, screenHeight] = screenResolution.split('x').map(Number)

  return {
    user_agent: userAgent,
    screen_resolution: screenResolution,
    pixel_ratio: pixelRatio,
    timezone: timezone.toString(),
    language: language,
    timestamp: timestamp,
    device_model: deviceModel,
    system_version: systemVersion,
    screen_width: screenWidth,
    screen_height: screenHeight,
    scale: pixelRatio,
    language_code: language.split('-')[0]
  }
}

async function parseAndSetUtmParams(referrer) {
  if (!referrer) return

  try {
    const decodedReferrer = decodeURIComponent(referrer)
    const urlParams = new URLSearchParams(decodedReferrer)
    const utmParams = {}

    const utmKeys = [
      'utm_source',
      'utm_campaign',
      'utm_medium',
      'utm_content',
      'utm_term',
      'utm_campaign_id'
    ]
    utmKeys.forEach((key) => {
      const value = urlParams.get(key)
      if (value) utmParams[key] = value
    })

    if (Object.keys(utmParams).length > 0) {
      const fingerprint = collectFingerprint()
      await todayAxios.post('/next/app/user/web/attribution/save', {
        fingerprint: JSON.stringify(fingerprint),
        ...utmParams
      })
    }
  } catch (error) {
    console.error('解析 referrer 参数失败:', error)
  }
}

async function init() {
  const UA = getUA()
  let queryParams = ''

  if (UA.isIOS && route.query.ct) {
    queryParams = `ct=${route.query.ct}&pt=126093046&mt=8`
  }

  if (UA.isIOS && route.query.referrer) {
    await parseAndSetUtmParams(route.query.referrer)
  }

  if (UA.isAndroid && route.query.referrer) {
    queryParams = `referrer=${encodeURIComponent(
      encodeURIComponent(route.query.referrer)
    )}`
  }

  switch (true) {
    case UA.isIOS:
      window.location.href = `${appStoreURL}${
        queryParams ? '&' + queryParams : ''
      }`
      break
    case UA.isAndroid:
      window.location.href = `${apkURL}&${queryParams}`
      break
    default:
      window.location.href = 'https://timemark.com/'
      break
  }
}
</script>

<template>
  <div class="pc-container" v-if="isPC">
    <header class="header">
      <a href="https://www.timemark.com" class="header-brand">
        <img src="../assets/logo.png" class="logo" />
        <span class="name">Timemark</span>
      </a>
    </header>
    <section class="hero">
      <div class="hero-content">
        <div class="text-group">
          <h1 class="title">Download the app to unlock the full experience</h1>
          <p class="desc">
            Timemark is built for the field. The mobile app stamps every photo
            with verified time and GPS the moment you capture it. Download the
            app if you haven't yet. It's
            <span style="font-weight: bold">the only way</span> to capture
            photos.
          </p>
        </div>
        <div class="qr-group">
          <div id="qrcode" class="qrcode"></div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
* {
  box-sizing: border-box;
}

.pc-container {
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  flex-direction: column;

  .header {
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;

    .header-brand {
      display: flex;
      align-items: center;
      text-decoration: none;

      .logo {
        width: 40px;
        height: 40px;
        border-radius: 6px;
        margin-right: 10px;
      }

      .name {
        font-size: 18px;
        font-weight: 700;
        color: #000;
        font-family: 'Plus Jakarta Sans', sans-serif;
      }
    }
  }

  .hero {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 20px;

    .hero-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 48px;

      .text-group {
        max-width: 720px;

        .title {
          font-size: 50px;
          font-weight: 800;
          color: #000;
          line-height: 1.2;
          margin: 0 0 24px 0;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }

        .desc {
          font-size: 18px;
          font-weight: 400;
          color: #000;
          line-height: 1.6;
          margin: 0;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
      }

      .qr-group {
        .qrcode {
          padding: 16px;
          background: #fff;
        }
      }
    }
  }
}
</style>
